import '../../styles/editcase/WorkflowStepper.css';

const STEPS = [
    { key: 'upload', label: 'Upload & Manage' },
    { key: 'results', label: 'Review & Approve' },
    { key: 'documents', label: 'Documents' },
];

// Home-health only: diagnosis-code review gets its own step between
// Review & Approve and Documents. The Documents stage means generated
// documents; code selection is a separate review job.
const CODING_STEP = { key: 'coding', label: 'Coding' };

export const STAGE_ORDER = Object.fromEntries(STEPS.map((s, i) => [s.key, i]));

/**
 * @param {string}   currentStage    – The stage the user is currently viewing
 * @param {string}   progressStage   – The furthest stage reached (data-driven)
 * @param {function} onStageClick    – Called with stage key when user clicks a reachable step
 * @param {Set}      completedStages – Stages explicitly marked as done (green checkmark)
 * @param {boolean}  showCodingStep  – Render the Coding step (home_health cases with codes)
 * @param {Set}      reachableStages – Step keys the user may click. Optional; when absent,
 *                                     every step up to progressStage is reachable. Needed
 *                                     once the Coding step exists, whose reachability is
 *                                     data-gated (codes present) rather than ordinal.
 */
export default function WorkflowStepper({ currentStage, progressStage, onStageClick, completedStages, showCodingStep, reachableStages }) {
    const steps = showCodingStep
        ? [STEPS[0], STEPS[1], CODING_STEP, STEPS[2]]
        : STEPS;
    const order = Object.fromEntries(steps.map((s, i) => [s.key, i]));
    const currentIdx = order[currentStage] ?? 0;
    const progressIdx = order[progressStage ?? currentStage] ?? 0;
    const done = completedStages || new Set();

    return (
        <div className="workflow-stepper">
            {steps.map((step, idx) => {
                const isReachable = reachableStages
                    ? reachableStages.has(step.key)
                    : idx <= progressIdx;
                const isViewing = idx === currentIdx;

                // Green checkmark only when explicitly in completedStages
                let status = 'upcoming';
                if (done.has(step.key)) status = 'completed';
                else if (isReachable) status = 'active';

                // Highlight whichever step the user is currently looking at
                const viewingClass = isViewing ? 'viewing' : '';

                const handleClick = () => {
                    if (isReachable && onStageClick) {
                        onStageClick(step.key);
                    }
                };

                return (
                    <div key={step.key} className="workflow-step-wrapper">
                        {idx > 0 && (
                            <div className={`workflow-connector ${idx <= progressIdx ? 'filled' : ''}`} />
                        )}
                        <div
                            className={`workflow-step ${status} ${viewingClass} ${isReachable ? 'clickable' : ''}`}
                            onClick={handleClick}
                        >
                            <div className="workflow-circle">
                                {status === 'completed' ? (
                                    <span className="workflow-check">&#10003;</span>
                                ) : (
                                    <span className="workflow-number">{idx + 1}</span>
                                )}
                            </div>
                            <span className="workflow-label">{step.label}</span>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
